import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Upload, Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { formatError, SITE_URL } from "@/lib/api";
import { uploadImage } from "./blogApi";

const slugify = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const firstPara = (html) => {
  const d = new DOMParser().parseFromString(html || "", "text/html");
  return d.querySelector("p")?.textContent || "";
};

const Input = ({ label, k, form, set, max, hint, type = "text", list, area, placeholder }) => {
  const v = form[k] || "";
  const props = { value: v, placeholder, list, onChange: (e) => set(k, e.target.value), "data-testid": `seo-${k}`, className: "mt-1 w-full rounded-xl border border-violet-200 px-3 py-2.5 text-sm" };
  return (
    <label className="block">
      <span className="flex justify-between text-xs font-bold uppercase tracking-widest text-[#581C87]">{label}
        {max && <span className={v.length > max ? "text-red-600" : "text-slate-500"} data-testid={`seo-${k}-count`}>{v.length}/{max}</span>}</span>
      {area ? <textarea rows={3} {...props} /> : <input type={type} {...props} />}
      {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
    </label>
  );
};

const ImageField = ({ label, k, form, set }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { set(k, await uploadImage(f)); } catch (ex) { toast.error(formatError(ex)); }
    setBusy(false);
  };
  return (
    <div>
      <span className="text-xs font-bold uppercase tracking-widest text-[#581C87]">{label}</span>
      {form[k] && <img src={form[k]} alt="" className="mt-2 aspect-[16/9] w-full rounded-xl object-cover" data-testid={`seo-${k}-preview`} />}
      <div className="mt-2 flex gap-2">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-[#F5F0FF] px-4 py-2 text-sm font-bold text-[#581C87] hover:bg-violet-200">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={pick} data-testid={`seo-${k}-upload`} />
        </label>
        {form[k] && <button type="button" onClick={() => set(k, "")} className="text-sm font-bold text-slate-500 hover:text-red-600" data-testid={`seo-${k}-remove`}>Remove</button>}
      </div>
    </div>
  );
};

const KeywordCheck = ({ form }) => {
  const kw = (form.focus_keyword || "").trim().toLowerCase();
  if (!kw) return <p className="text-sm text-slate-500">Add a primary keyword to see where it appears.</p>;
  const checks = [
    ["SEO title", (form.meta_title || form.title || "").toLowerCase().includes(kw)],
    ["URL slug", (form.slug || "").includes(slugify(kw))],
    ["Meta description", (form.meta_description || "").toLowerCase().includes(kw)],
    ["Opening paragraph", firstPara(form.body_html).toLowerCase().includes(kw)],
  ];
  return (
    <ul className="space-y-1.5" data-testid="seo-keyword-check">
      {checks.map(([l, ok]) => (
        <li key={l} className={`flex items-center gap-2 text-sm font-semibold ${ok ? "text-emerald-700" : "text-slate-500"}`} data-testid={`seo-check-${slugify(l)}`}>
          {ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}{l}
        </li>
      ))}
    </ul>
  );
};

const H = ({ children }) => <h3 className="border-t border-violet-100 pt-5 text-sm font-extrabold text-[#13052A]">{children}</h3>;

export const SeoPanel = ({ open, onOpenChange, form, set, categories }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="seo-panel">
      <SheetHeader>
        <SheetTitle>SEO & article settings</SheetTitle>
        <SheetDescription>Changes save automatically.</SheetDescription>
      </SheetHeader>
      <div className="mt-6 space-y-4">
        <Input label="SEO title tag" k="meta_title" form={form} set={set} max={60} placeholder={form.title} hint="Shown in Google results. Defaults to the article title." />
        <Input label="Meta description" k="meta_description" form={form} set={set} max={160} area />
        <Input label="URL slug" k="slug" form={form} set={(k, v) => set(k, slugify(v))} hint={`${SITE_URL}/blog/${form.slug || ""}`} />
        <Input label="Primary target keyword" k="focus_keyword" form={form} set={set} />
        <KeywordCheck form={form} />
        <H>Article details</H>
        <Input label="Category" k="category" form={form} set={set} list="blog-categories" />
        <datalist id="blog-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
        <Input label="Author" k="author" form={form} set={set} />
        <Input label="Publication date" k="published_at" type="date" form={form} set={set} hint="Leave empty to use the day you publish." />
        <ImageField label="Featured image" k="image" form={form} set={set} />
        <Input label="Featured image alt text" k="image_alt" form={form} set={set} />
        <H>Sharing & canonical</H>
        <Input label="Canonical URL" k="canonical" form={form} set={set} placeholder={`${SITE_URL}/blog/${form.slug || ""}`} hint="Leave empty to use the article's own address." />
        <Input label="Open Graph title" k="og_title" form={form} set={set} placeholder={form.meta_title || form.title} />
        <ImageField label="Open Graph image" k="og_image" form={form} set={set} />
        <H>End-of-article call to action</H>
        <Input label="Heading" k="cta_heading" form={form} set={set} placeholder="Ready to take the next step?" />
        <Input label="Short text" k="cta_note" form={form} set={set} />
        <Input label="Button label" k="cta_label" form={form} set={set} placeholder="Explore the related service" />
        <Input label="Button link" k="cta_to" form={form} set={set} placeholder="/contact" />
      </div>
    </SheetContent>
  </Sheet>
);
